import styled from "@emotion/styled";
import { Badge, IconButton, Stack, tooltipClasses, Typography, Popper, useTheme } from "@mui/material";
import React, { useRef, useEffect } from "react";
import HyroMultiLanguageField from "src/components/hyro-components/fields/multi-language/HyroMultiLanguageField";
import { FIELD_TYPES } from "@hyro-components/fields/utils";
import HyroMultiSelect from "@hyro-components/fields/multi-select/HyroMultiSelect";
import HyroRadioGroup from "@hyro-components/fields/radio-group/HyroRadioGroup";
import Secret from "@hyro-components/fields/secret/Secret";
import HyroSingleSelect from "@hyro-components/fields/single-option/HyroSingleSelect";
import AddSuggestionIcon from "src/icons/AddSuggestionIcon";
import ListField from "@hyro-components/fields/list-field/HyroListField";
import { getLanguageIcon } from "../../collections/consts";
import StatusChip from "src/components/collections/StatusChip";
import FieldWrapper from "@hyro-components/fields/FieldWrapper";
import Tooltip from "@hyro-components/Tooltip";
import HyroFileUpload from "@hyro-components/hyro-file-upload/HyroFileUpload";
import HyroTextField from "./text-field/HyroTextField";
import { selectedAssistantSelector } from "src/stores/selectors/appSelectors";
import useAppStore from "src/stores/appStore";
import { ASSISTANT_CHANNELS } from "@consts";
import { useFlags } from "launchdarkly-react-client-sdk";
import ExpressionTag from "@hyro-components/ExpressionTag";

const HyroField = props => {
  const {
    fieldType,
    label,
    field,
    value,
    required,
    onChange,
    onBlur,
    onFocus,
    error,
    isReadOnly,
    status,
    isFloatingLabel = false,
    onAddButtonClick,
    shouldShowAddButton = false,
    isMultiLine = false,
    maxRows = undefined,
    minRows = undefined,
    index = 0,
    options,
    isFieldReadOnlyFromSpec,
    currentSelectedLanguage,
    shouldShowLanguageFlag = false,
    languageFlagPosition = "end",
    showErrorInPopper = false,
    popperErrorPlacement = "bottom-start",
    customErrorPopperOffset = null,
    isDrawerView = false,
    testId,
    fetchingRefs,
    autoFocus = false,
    InputProps,
    helperText,
    placeholder,
    errorPopperWidth,
    formHelperProps,
    showTopSection,
    showCopyButton = true,
    showErrorMessage = true,
    maxLength,
    expressionable = false,
    usesExpression = false,
    onExpressionClick,
    onRevertToDefault,
  } = props;
  const { enableExpressionDialogV2 } = useFlags();
  const assistant = useAppStore(selectedAssistantSelector);
  const isVoiceAssistant = assistant?.channel === ASSISTANT_CHANNELS.VOICE || assistant?.channel === ASSISTANT_CHANNELS.PHONE;
  const theme = useTheme();
  const LanguageIcon = getLanguageIcon(currentSelectedLanguage);
  const [inputAnchorEl, setInputAnchorEl] = React.useState(null);

  const getHelperText = () => {
    const getOffset = () => {
      if (fieldType === FIELD_TYPES.MULTI_LANGUAGE_TEXT) {
        return [shouldShowLanguageFlag ? -40 : 0, 12];
      }

      if (fieldType === FIELD_TYPES.CHOOSE_ONE) {
        return [0, 24];
      }
      return [0, 12];
    };

    if (error && typeof error !== "object" && showErrorMessage) {
      if (showErrorInPopper) {
        const offset = customErrorPopperOffset || getOffset();

        return (
          <>
            <StyledHelperPopper
              data-testid={`${testId ? `${testId}-` : ""}${fieldType}-${index}-helper`}
              open={!!error && !!inputAnchorEl}
              anchorEl={inputAnchorEl}
              sx={{ width: errorPopperWidth ?? inputAnchorEl?.clientWidth }}
              placement={popperErrorPlacement}
              modifiers={[{ name: "offset", options: { offset } }]}>
              <Typography variant="caption">
                {error}
              </Typography>
            </StyledHelperPopper>
            {helperText && <Typography variant="caption" color="grey.600">{helperText}</Typography>}
          </>
        );
      }

      return (
        <Typography sx={{ whiteSpace: "pre-line" }} variant="caption">
          {error}
        </Typography>
      );
    }

    const expressionTag = usesExpression ? <ExpressionTag /> : null;

    if (status || expressionTag) {
      return (
        <Stack component="span" direction="row" justifyContent="space-between" alignItems="center" width="100%">
          {status ? <StatusChip status={status} /> : <span />}
          {expressionTag}
        </Stack>
      );
    }

    if (helperText) {
      return <Typography variant="caption" color="grey.600">{helperText}</Typography>;
    }

    return null;
  };

  const getTextFieldProps = () => ({
    fullWidth: true,
    maxRows,
    minRows,
    multiline: isMultiLine,
    variant: "outlined",
    label: isFloatingLabel ? label : "",
    required,
    error: !!error,
    helperText: getHelperText(),
    readOnly: isReadOnly,
    isFieldReadOnlyFromSpec,
    placeholder: placeholder || label,
    fieldKey: field?.key,
    onFocus: (e) => {
      onFocus?.();

      if (showErrorInPopper) {
        setInputAnchorEl(e.currentTarget);
      }
    },
    showCopyButton
  });
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const input = inputRef.current;
      input?.focus();
      input?.setSelectionRange(input.value.length, input.value.length); // Move cursor to the end
    }
  }, [autoFocus]);

  const getTestIdObject = () => ({
    FormHelperTextProps: {
      "data-testid": `${testId ? `${testId}-` : ""}${fieldType}-${index}-helper`,
      sx: showErrorInPopper && !helperText ? { display: "none" } : { ...formHelperProps?.sx }
    },
    inputProps: { autoComplete: "off", "data-testid": `${testId ? `${testId}-` : ""}${fieldType}-${index}` }
  });

  const getFieldDisplay = () => {
    switch (fieldType) {
      case FIELD_TYPES.BOOL:
        return (
          <HyroRadioGroup
            {...props}
            options={[
              { key: true, label: "Yes" },
              { key: false, label: "No" }
            ]}
            onChange={(val) => onChange(val === "true")}
          />
        );
      case FIELD_TYPES.CHOOSE_AT_LEAST_ONE:
        return <HyroMultiSelect
          {...props}
          helperText={getHelperText()}
          testId={`${testId ? `${testId}-` : ""}${fieldType}-${index}`}
          onFocus={(target) => {
            onFocus?.();

            if (showErrorInPopper) {
              setInputAnchorEl(target);
            }
          }}
          onBlur={() => {
            onBlur?.();

            if (showErrorInPopper) {
              setInputAnchorEl(null);
            }
          }}
          onPopperClose={() => {
            if (showErrorInPopper) {
              setInputAnchorEl(null);
            }
          }}
        />;
      case FIELD_TYPES.CHOOSE_ONE:
        return <HyroSingleSelect {...props}
          inputRef={inputRef}
          helperText={getHelperText()}
          testId={`${testId ? `${testId}-` : ""}${fieldType}-${index}`}
          options={options?.length ? options : []}
          onAddNewCollection={null}
          onBlur={() => {
            onBlur?.();;
            if (showErrorInPopper) {
              setInputAnchorEl(null);
            }
          }}
          onFocus={(target) => {
            onFocus?.();

            if (showErrorInPopper) {
              setInputAnchorEl(target);
            }
          }}
          onPopperClose={() => {
            if (showErrorInPopper) {
              setInputAnchorEl(null);
            }
          }}
        />;
      case FIELD_TYPES.COLLECTION_REF:
        return <HyroSingleSelect {...props}
          helperText={getHelperText()}
          loading={fetchingRefs}
          testId={`${testId ? `${testId}-` : ""}${fieldType}-${index}`}
          options={options?.length ? options : []}
        />;
      case FIELD_TYPES.SECRET:
        return <Secret {...props} />;
      case FIELD_TYPES.LIST:
        return (
          <ListField
            {...props}
            index={index}
            inputRef={inputRef}
            helperText={getHelperText()}
            values={value || []}
            onChange={(e, value) => onChange(value, e)}
            onDelete={valueToDelete => () => {
              const newValue = value.filter(
                value => value !== valueToDelete
              );
              onChange(newValue);
            }}
            onBlur={() => {
              onBlur?.();

              if (showErrorInPopper) {
                setInputAnchorEl(null);
              }
            }}
            onFocus={(e) => {
              onFocus?.();

              if (showErrorInPopper) {
                setInputAnchorEl(e.target);
              }
            }}
          />
        );
      case FIELD_TYPES.ACTION:
        return (
          <Stack flexDirection={"row"} gap={"16px"} alignItems={"flex-start"}>
            <HyroTextField
              {...getTextFieldProps()}
              {...getTestIdObject()}
              showPlayerButton={isVoiceAssistant}
              showExpressionButton={expressionable && enableExpressionDialogV2}
              usesExpression={usesExpression}
              onExpressionClick={onExpressionClick}
              onRevertToDefault={onRevertToDefault}
              InputProps={{
                readOnly: isReadOnly,
                endAdornment: (shouldShowLanguageFlag &&
                                    <LanguageIcon style={{ marginLeft: "12px" }} />)
              }}
              value={usesExpression ? value : (value?.text ?? "")}
              onChange={({ target }) => !isReadOnly && onChange({ ...value, text: target.value })}
              onBlur={e => {
                const trimmedValue = e.target.value.trim();
                !isReadOnly && onChange({ ...value, text: trimmedValue });
              }}
            />
            {
              shouldShowAddButton &&
                            <Tooltip
                              title={isReadOnly ? "View buttons" : "Add a button"}
                              placement={"top"}
                            >
                              <AddSuggestionBadgeContainer
                                data-testid={`add-suggestion-badge-${index}`}
                                badgeContent={(value?.suggestions ?? []).length}
                                sx={{ marginTop: "8px" }}
                                color="secondary">
                                <AddSuggestionButton data-testid={`add-suggestion-button-${index}`} onClick={onAddButtonClick}>
                                  <AddSuggestionIcon color={theme.palette.primary[500]} />
                                </AddSuggestionButton>
                              </AddSuggestionBadgeContainer>
                            </Tooltip>
            }
          </Stack>);
      case FIELD_TYPES.MULTI_LANGUAGE_TEXT:
        if (isDrawerView) {
          return <HyroMultiLanguageField
            {...props}
            title={field?.spec?.display_key}
            isMandatory={field?.spec?.is_mandatory}
            value={field?.configuration}
            errorText={error}
            textFieldProps={getTextFieldProps()}
          />;
        }

        return (
          <HyroTextField
            {...getTextFieldProps()}
            {...getTestIdObject()}
            {...props}
            inputRef={inputRef}
            InputProps={{
              readOnly: isReadOnly,
              startAdornment: (shouldShowLanguageFlag && languageFlagPosition === "start" &&
                                <LanguageIcon style={{ marginRight: "8px" }} />),
              endAdornment: (shouldShowLanguageFlag && languageFlagPosition === "end" &&
                                <LanguageIcon style={{ marginLeft: "12px" }} />)
            }}
            value={value?.[currentSelectedLanguage] ?? ""}
            onChange={({ target }) => !isReadOnly && onChange({ ...value, [currentSelectedLanguage]: target.value })}
            onBlur={e => !isReadOnly && onChange({ ...value, [currentSelectedLanguage]: e.target.value.trim() })}
            onFocus={(e) => {
              onFocus?.();

              if (showErrorInPopper) {
                setInputAnchorEl(e.currentTarget);
              }
            }}
          />
        );
      case FIELD_TYPES.FILE:
        return <HyroFileUpload
          value={value}
          acceptedFileTypes={field?.allowed_file_types}
          variant="field"
          error={error}
          isReadOnly={isReadOnly}
          helperText={getHelperText()}
          onSubmit={onChange}
        />;
      default:
        return <HyroTextField
          {...props}
          {...getTextFieldProps()}
          {...getTestIdObject()}
          inputRef={inputRef}
          maxLength={maxLength || 34}
          InputProps={{ sx: { ...InputProps?.sx }, disabled: isReadOnly }}
          onChange={({ target: { value } }) => !isReadOnly && onChange(value)}
          onBlur={e => {
            !isReadOnly && onChange(e.target.value.trim());
            onBlur?.();

            if (showErrorInPopper) {
              setInputAnchorEl(null);
            }
          }}
        />;
    }
  };
  if (showTopSection) {
    return <FieldWrapper {...props}>
      {getFieldDisplay()}
    </FieldWrapper>;
  }
  return getFieldDisplay();
};

export default HyroField;

const StyledHelperPopper = styled(Popper)`
  background-color: #fff;
  box-shadow: ${({ theme }) => theme.shadows[4]};
  padding: 12px 16px;
  border-radius: 8px;
  color: #EF404B;
  white-space: pre-line;
  margin-top: 8px;
`;

const AddSuggestionBadgeContainer = styled(Badge)`
  height: fit-content;
`;

const AddSuggestionButton = styled(IconButton)`
  border: 1px solid #BCC2FB;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: white;
  cursor: pointer;

  &:hover {
    border: 1px solid #BCC2FB;
    background-color: ${({ theme }) => theme.palette.tertiary[300]};
  }

  &:active {
    border: 1px solid #BCC2FB;
    background-color: ${({ theme }) => theme.palette.tertiary[300]};
  }

  & .MuiTouchRipple - root {
  display: none;
}
`;
